// client/src/useWorldToScreenFromCv.ts
import { useMemo } from "react";

export type WorldToScreenFn = (x: number, y: number) => { u: number; v: number };

/**
 * Very small least-squares homography fit:
 *   world (x,y)  ->  screen (u,v)
 *
 * We assume a projective transform:
 *   u = (h11 x + h12 y + h13) / (h31 x + h32 y + 1)
 *   v = (h21 x + h22 y + h23) / (h31 x + h32 y + 1)
 *
 * with h33 fixed to 1. That gives 8 unknowns.
 *
 * For each correspondence we get two linear equations in those 8 unknowns,
 * and we solve in least squares via normal equations + Gaussian elimination.
 */
export function useWorldToScreenFromCv(cv: any | null): WorldToScreenFn | null {
  return useMemo(() => {
    if (!cv?.sessions?.length) return null;

    const pairs: { x: number; y: number; u: number; v: number }[] = [];
    const MAX_PAIRS = 800;

    try {
      const session = cv.sessions[0];
      const rallies = session.rallies ?? [];

      outer: for (const rally of rallies) {
        const frames = rally.frames ?? [];
        for (const frame of frames) {
          const pcs = frame.player_court_positions ?? [];
          const pts = frame.court?.player_points ?? [];

          for (let i = 0; i < Math.min(pcs.length, pts.length); i++) {
            const pc = pcs[i];
            const pt = pts[i];
            if (!pc || !pt) continue;
            if (typeof pc.x !== "number" || typeof pc.y !== "number") continue;
            if (typeof pt.u !== "number" || typeof pt.v !== "number") continue;
            if (pt.confidence != null && pt.confidence < 0.85) continue;

            pairs.push({ x: pc.x, y: pc.y, u: pt.u, v: pt.v });
            if (pairs.length >= MAX_PAIRS) break outer;
          }
        }
      }
    } catch {
      return null;
    }

    if (pairs.length < 6) {
      // Not enough data to fit a projective transform
      return null;
    }

    const nParams = 8; // h11,h12,h13,h21,h22,h23,h31,h32 (h33=1)
    const AtA: number[][] = Array.from({ length: nParams }, () =>
      Array(nParams).fill(0)
    );
    const Atb: number[] = Array(nParams).fill(0);

    const accumulateRow = (row: number[], y: number) => {
      for (let i = 0; i < nParams; i++) {
        const ri = row[i];
        Atb[i] += ri * y;
        for (let j = 0; j < nParams; j++) {
          AtA[i][j] += ri * row[j];
        }
      }
    };

    for (const { x, y, u, v } of pairs) {
      // Row for u-equation:
      // x h11 + y h12 + 1 h13 - u x h31 - u y h32 = u
      const rowU = [x, y, 1, 0, 0, 0, -u * x, -u * y];
      accumulateRow(rowU, u);

      // Row for v-equation:
      // x h21 + y h22 + 1 h23 - v x h31 - v y h32 = v
      const rowV = [0, 0, 0, x, y, 1, -v * x, -v * y];
      accumulateRow(rowV, v);
    }

    // Solve AtA * p = Atb via Gaussian elimination with partial pivoting.
    const solveLinearSystem = (
      A: number[][],
      b: number[]
    ): number[] | null => {
      const n = b.length;
      const a = A.map((row) => row.slice());
      const x = b.slice();

      for (let i = 0; i < n; i++) {
        // Pivot
        let pivot = i;
        let maxAbs = Math.abs(a[i][i]);
        for (let r = i + 1; r < n; r++) {
          const val = Math.abs(a[r][i]);
          if (val > maxAbs) {
            maxAbs = val;
            pivot = r;
          }
        }
        if (maxAbs < 1e-10) {
          return null; // singular
        }
        if (pivot !== i) {
          [a[i], a[pivot]] = [a[pivot], a[i]];
          [x[i], x[pivot]] = [x[pivot], x[i]];
        }

        const diag = a[i][i];
        const invDiag = 1 / diag;

        // Normalize row i
        for (let j = i; j < n; j++) {
          a[i][j] *= invDiag;
        }
        x[i] *= invDiag;

        // Eliminate other rows
        for (let r = 0; r < n; r++) {
          if (r === i) continue;
          const factor = a[r][i];
          if (Math.abs(factor) < 1e-12) continue;
          for (let j = i; j < n; j++) {
            a[r][j] -= factor * a[i][j];
          }
          x[r] -= factor * x[i];
        }
      }

      return x;
    };

    const p = solveLinearSystem(AtA, Atb);
    if (!p) return null;

    const [h11, h12, h13, h21, h22, h23, h31, h32] = p;
    const h33 = 1;

    const fn: WorldToScreenFn = (x, y) => {
      const denom = h31 * x + h32 * y + h33;
      if (Math.abs(denom) < 1e-6) {
        return { u: 0.5, v: 0.5 };
      }
      const u = (h11 * x + h12 * y + h13) / denom;
      const v = (h21 * x + h22 * y + h23) / denom;
      // Clamp to [0,1] just to be safe
      return {
        u: Math.min(1, Math.max(0, u)),
        v: Math.min(1, Math.max(0, v)),
      };
    };

    return fn;
  }, [cv]);
}
